import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import { RefreshCw, Search, Package } from 'lucide-react';

const STATUS_COLORS = {
  draft: '#ffb020', stored: '#4d90fe', picked_up: '#8BC63E',
  pending: '#8789a0', expired: '#ff4466', cancelled: '#8a8da4',
};

// Times come from the server in Georgian time without a zone: show them as-is.
const fmt = (iso) => (iso ? iso.replace('T', ' ').slice(0, 16) : '—');
const PAGE = 50;

export default function ParcelHistory() {
  const [parcels, setParcels] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Search runs on the server across ALL parcels (order, name, phone, email, pick code).
  const load = async (append = false) => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: PAGE, offset: append ? parcels.length : 0 };
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await adminAPI.parcelHistory(params);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setParcels(append ? [...parcels, ...items] : items);
      setTotal(Array.isArray(r.data) ? items.length : (r.data.total || 0));
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load parcels');
    }
    setLoading(false);
  };
  useEffect(() => {
    const t = setTimeout(() => load(false), search ? 350 : 0);
    return () => clearTimeout(t);
  }, [search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}><Package size={20} /> Parcel History ({total})</h1>
        <button style={s.refreshBtn} onClick={() => load(false)}><RefreshCw size={14} /> Refresh</button>
      </div>
      <div style={s.filters}>
        <div style={s.searchBox}>
          <Search size={14} style={{color:'#8a8da4'}} />
          <input style={s.searchInput} placeholder="Search order, name, phone, email or pick code..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={s.statusTabs}>
          {['all','draft','stored','picked_up','cancelled','expired'].map(st => (
            <button key={st} style={{...s.tab, ...(statusFilter === st ? s.tabActive : {})}}
              onClick={() => setStatusFilter(st)}>
              {st === 'all' ? 'All' : st.replace(/_/g,' ')}
            </button>
          ))}
        </div>
      </div>
      {error && <div style={s.error}>{error}</div>}
      <div style={s.panel}>
        {loading && parcels.length === 0 ? <div style={s.empty}>Loading...</div> :
         parcels.length === 0 ? <div style={s.empty}>No parcels found</div> :
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Order</th><th style={s.th}>Sender</th><th style={s.th}>Recipient</th>
            <th style={s.th}>Pick code</th>
            <th style={s.th}>Device</th><th style={s.th}>Box</th><th style={s.th}>Status</th>
            <th style={s.th}>Created</th><th style={s.th}>Stored</th><th style={s.th}>Picked Up</th>
          </tr></thead>
          <tbody>
            {parcels.map(p => (
              <tr key={p.id || p.order_no}>
                <td style={s.td}><span style={{fontFamily:"'Space Mono',monospace",fontSize:11}}>{p.order_no}</span></td>
                <td style={s.td}>{p.sender_name}</td>
                <td style={s.td}>{p.recipient_name}<br/><span style={{fontSize:11,color:'#8a8da4'}}>{[p.recipient_phone, p.recipient_email].filter(Boolean).join(' · ')}</span></td>
                <td style={s.td}><span style={{fontFamily:"'Space Mono',monospace",fontSize:12}}>{p.pick_code || '—'}</span></td>
                <td style={s.td}><span style={{fontSize:12}}>{p.device_name || p.device_number}</span></td>
                <td style={s.td}>{p.box_name || '—'}</td>
                <td style={s.td}>
                  <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,
                    background:`${STATUS_COLORS[p.status]||'#8a8da4'}18`,color:STATUS_COLORS[p.status]||'#8a8da4'}}>
                    {p.status.replace(/_/g,' ')}
                  </span>
                </td>
                <td style={s.td}><span style={{fontSize:11,color:'#8789a0'}}>{fmt(p.created_at)}</span></td>
                <td style={s.td}><span style={{fontSize:11,color:'#8789a0'}}>{fmt(p.stored_at)}</span></td>
                <td style={s.td}><span style={{fontSize:11,color:'#8789a0'}}>{fmt(p.picked_up_at)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
      {parcels.length < total && (
        <button style={{...s.refreshBtn, margin:'14px auto 0'}} disabled={loading} onClick={() => load(true)}>
          {loading ? 'Loading...' : `Show more (${parcels.length} of ${total})`}
        </button>
      )}
    </div>
  );
}

const s = {
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 },
  title: { fontSize:22, fontWeight:700, color:'#e4e5ed', display:'flex', alignItems:'center', gap:10 },
  refreshBtn: { display:'flex', alignItems:'center', gap:6, padding:'8px 16px', background:'transparent', border:'1px solid #252733', borderRadius:8, color:'#8789a0', fontSize:12, cursor:'pointer' },
  filters: { display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' },
  searchBox: { display:'flex', alignItems:'center', gap:8, background:'#0f1117', border:'1px solid #252733', borderRadius:8, padding:'8px 14px', flex:1, minWidth:200 },
  searchInput: { background:'transparent', border:'none', outline:'none', color:'#e4e5ed', fontSize:13, flex:1 },
  statusTabs: { display:'flex', gap:4 },
  tab: { padding:'7px 14px', borderRadius:8, border:'1px solid #252733', background:'transparent', color:'#8789a0', fontSize:12, fontWeight:500, cursor:'pointer', textTransform:'capitalize' },
  tabActive: { background:'rgba(139,198,62,0.1)', borderColor:'rgba(139,198,62,0.3)', color:'#8BC63E' },
  panel: { background:'#0f1117', border:'1px solid #252733', borderRadius:12, overflow:'auto' },
  table: { width:'100%', borderCollapse:'collapse', minWidth:900 },
  th: { textAlign:'left', padding:'10px 14px', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:0.8, color:'#565870', background:'#0a0b0e', borderBottom:'1px solid #252733' },
  td: { padding:'10px 14px', fontSize:13, borderBottom:'1px solid #1a1b25', color:'#e4e5ed' },
  empty: { padding:60, textAlign:'center', color:'#8a8da4', fontSize:14 },
  error: { background:'rgba(255,68,102,0.1)', border:'1px solid rgba(255,68,102,0.3)', borderRadius:8, padding:'10px 14px', color:'#ff4466', fontSize:13, marginBottom:12 },
};
